// src/app/password-reset.jsx
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

export default function PasswordReset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState('checking'); // checking, valid, invalid, expired
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenStatus('invalid');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/validate-confirmation-token?token=${token}`);
        
        if (response.data.valid) {
          setTokenStatus('valid');
          setUserInfo(response.data.user);
        } else {
          setTokenStatus(response.data.expired ? 'expired' : 'invalid');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenStatus('invalid');
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async () => {
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
    if (!specialCharRegex.test(password)) {
      alert('Password must contain at least one special character');
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/confirm-account-with-password`, {
        token,
        password
      });

      if (response.data.success) {
        alert('Password reset successfully! You can now log in with your new password.');
        // Force a page refresh when navigating to login to clear any cached state
        window.location.href = '/login';
      } else {
        alert('Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tokenStatus === 'checking') {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Validating reset link...</Text>
      </View>
    );
  }

  if (tokenStatus === 'invalid') {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Invalid Reset Link</Text>
        <Text style={styles.message}>
          This password reset link is invalid or has been used already.
        </Text>
        <TouchableOpacity 
          style={styles.button} 
          onPress={() => navigate('/login')}
        >
          <Text style={styles.buttonText}>Go to Login</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (tokenStatus === 'expired') {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Link Expired</Text>
        <Text style={styles.message}>
          This password reset link has expired. Please request a new password reset from the login page.
        </Text>
        <TouchableOpacity 
          style={styles.button} 
          onPress={() => navigate('/login')}
        >
          <Text style={styles.buttonText}>Go to Login</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <Text style={styles.title}>Reset Your Password</Text>
      <Text style={styles.subtitle}>
        Hello {userInfo?.firstName} {userInfo?.lastName}! Please enter your new password below.
      </Text>

      <Text style={styles.label}>New Password</Text>
      <TextInput
        style={styles.input}
        value={password}
        onChangeText={setPassword}
        secureTextEntry
        placeholder="Enter your new password"
        autoComplete="new-password"
      />

      <Text style={styles.label}>Confirm New Password</Text>
      <TextInput
        style={styles.input}
        value={confirmPassword}
        onChangeText={setConfirmPassword}
        secureTextEntry
        placeholder="Confirm your new password"
        autoComplete="new-password"
      />

      <TouchableOpacity
        style={[styles.button, loading && styles.buttonDisabled]}
        onPress={handleSubmit}
        disabled={loading}
      >
        <Text style={styles.buttonText}>
          {loading ? 'Resetting Password...' : 'Reset Password'}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.linkButton}
        onPress={() => navigate('/login')}
      >
        <Text style={styles.linkText}>Back to Login</Text>
      </TouchableOpacity>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f8f9fa',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 30,
    textAlign: 'center',
    lineHeight: 22,
  },
  message: {
    fontSize: 16,
    color: '#666',
    marginBottom: 30,
    textAlign: 'center',
    lineHeight: 22,
  },
  label: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
    alignSelf: 'flex-start',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    fontSize: 16,
    marginBottom: 20,
    backgroundColor: '#fff',
  },
  button: {
    width: '100%',
    backgroundColor: '#007AFF',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  linkButton: {
    padding: 10,
  },
  linkText: {
    color: '#007AFF',
    fontSize: 16,
  },
});